import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { Bell } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { api } from '../../api/client';
import { useAction } from '../../api/hooks';
import { useCrumbs } from '../../layout/crumbs';
import { Button } from '../../ui/Button';
import { EmptyState, QueryView } from '../../ui/feedback';
import { LoadMore, PageHeader, Tabs, usePageTitle } from '../../ui/misc';
import { Modal } from '../../ui/Modal';
import { Timestamp } from '../../ui/Timestamp';

interface Note {
  id: string;
  kind: string;
  title: string;
  body: string;
  link: string;
  createdAt: string;
  readAt: string | null;
}

export function BrowserNotificationButton({ size = 'md' }: { size?: 'sm' | 'md' }) {
  const [permission, setPermission] = useState<string>(() => {
    if (typeof window === 'undefined' || !('Notification' in window)) return 'unsupported';
    return Notification.permission;
  });

  if (permission === 'unsupported' || permission === 'granted') return null;

  const request = async () => {
    if (typeof window === 'undefined' || !('Notification' in window)) return;
    try {
      const res = await Notification.requestPermission();
      setPermission(res);
    } catch {
      // ignore
    }
  };

  if (permission === 'denied') {
    return (
      <span className="small muted" title="Notifications are blocked in browser settings">
        Notifications blocked
      </span>
    );
  }

  return (
    <Button variant="secondary" size={size === 'sm' ? 'sm' : undefined} onClick={request}>
      Enable browser notifications
    </Button>
  );
}

/** The inbox shared by the page and the top-bar dialog. `onOpened` runs before navigating to a notification. */
function useInbox(unreadOnly: boolean, onOpened?: () => void) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const q = useInfiniteQuery({
    queryKey: ['notifications', unreadOnly],
    queryFn: ({ pageParam }) =>
      api.get<{ items: Note[]; unread: number; nextCursor: string | null }>(
        `/notifications?unread=${unreadOnly}${pageParam ? `&cursor=${encodeURIComponent(pageParam)}` : ''}`,
      ),
    initialPageParam: '',
    getNextPageParam: (l) => l.nextCursor ?? undefined,
  });
  const readAll = useAction(() => api.post('/notifications/read', { ids: 'all' }), {
    success: 'All marked as read',
    invalidate: [['notifications'], ['me']],
  });
  const open = async (n: Note) => {
    // Optimistically update caches so clicked item disappears immediately from unread list
    qc.setQueriesData<{
      pages: { items: Note[]; unread: number; nextCursor: string | null }[];
      pageParams: unknown[];
    }>({ queryKey: ['notifications', true] }, (old) => {
      if (!old) return old;
      return {
        ...old,
        pages: old.pages.map((p) => ({
          ...p,
          unread: Math.max(0, p.unread - 1),
          items: p.items.filter((item) => item.id !== n.id),
        })),
      };
    });
    qc.setQueriesData<{
      pages: { items: Note[]; unread: number; nextCursor: string | null }[];
      pageParams: unknown[];
    }>({ queryKey: ['notifications', false] }, (old) => {
      if (!old) return old;
      return {
        ...old,
        pages: old.pages.map((p) => ({
          ...p,
          unread: Math.max(0, p.unread - 1),
          items: p.items.map((item) =>
            item.id === n.id ? { ...item, readAt: new Date().toISOString() } : item,
          ),
        })),
      };
    });
    try {
      const r = await api.post<{ link: string }>(`/notifications/${n.id}/open`);
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['me'] }),
        qc.invalidateQueries({ queryKey: ['notifications'] }),
      ]);
      onOpened?.();
      navigate(r.link);
    } catch {
      await qc.invalidateQueries({ queryKey: ['notifications'] });
    }
  };
  return { q, readAll, open };
}

function InboxTabs({
  unreadOnly,
  unread,
  onChange,
}: {
  unreadOnly: boolean;
  unread: number | undefined;
  onChange: (unreadOnly: boolean) => void;
}) {
  return (
    <Tabs
      label="Notification filter"
      value={unreadOnly ? 'unread' : 'all'}
      onChange={(v) => onChange(v === 'unread')}
      tabs={[
        { value: 'unread', label: 'Unread', count: unread },
        { value: 'all', label: 'All' },
      ]}
    />
  );
}

function InboxList({ inbox, unreadOnly }: { inbox: ReturnType<typeof useInbox>; unreadOnly: boolean }) {
  const { q, open } = inbox;
  return (
    <QueryView query={q}>
      {(d) => {
        const items = d.pages.flatMap((p) => p.items);
        if (!items.length)
          return <EmptyState icon={Bell} title={unreadOnly ? 'You are all caught up' : 'No notifications'} />;
        return (
          <>
            <div className="table-wrap">
              {items.map((n) => (
                <button
                  key={n.id}
                  onClick={() => open(n)}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '10px minmax(0,1fr) auto',
                    gap: 12,
                    width: '100%',
                    textAlign: 'left',
                    padding: 16,
                    border: 'none',
                    borderBottom: '1px solid var(--color-hairline-soft)',
                    background: 'transparent',
                    cursor: 'pointer',
                    font: 'inherit',
                  }}
                >
                  <span
                    aria-label={n.readAt ? 'Read' : 'Unread'}
                    style={{
                      width: 8,
                      height: 8,
                      marginTop: 6,
                      borderRadius: 9999,
                      background: n.readAt ? 'transparent' : 'var(--color-primary)',
                    }}
                  />
                  <span className="stack" style={{ gap: 2 }}>
                    <span className={n.readAt ? '' : 'strong'}>{n.title}</span>
                    {n.body && (
                      <span
                        className="small muted"
                        style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                      >
                        {n.body}
                      </span>
                    )}
                  </span>
                  <span className="small muted">
                    <Timestamp value={n.createdAt} relative />
                  </span>
                </button>
              ))}
            </div>
            <LoadMore
              hasMore={!!q.hasNextPage}
              onClick={() => q.fetchNextPage()}
              loading={q.isFetchingNextPage}
            />
          </>
        );
      }}
    </QueryView>
  );
}

/** TKT-08 inbox: access is rechecked when listing and when opening. */
export function NotificationsPage() {
  usePageTitle('Notifications');
  useCrumbs([{ label: 'Workspace' }, { label: 'Notifications' }]);
  const [params, setParams] = useSearchParams();
  const unreadOnly = params.get('filter') !== 'all';
  const inbox = useInbox(unreadOnly);
  return (
    <div className="page narrow">
      <PageHeader
        title="Notifications"
        subtitle="Mentions, state changes, approval requests and delivery problems."
        actions={
          <div className="row nowrap" style={{ gap: 8 }}>
            <BrowserNotificationButton />
            <Button
              variant="secondary"
              onClick={() => inbox.readAll.mutate(undefined)}
              loading={inbox.readAll.isPending}
            >
              Mark all as read
            </Button>
          </div>
        }
      />
      <InboxTabs
        unreadOnly={unreadOnly}
        unread={inbox.q.data?.pages[0]?.unread}
        onChange={(u) => setParams(u ? {} : { filter: 'all' }, { replace: true })}
      />
      <InboxList inbox={inbox} unreadOnly={unreadOnly} />
    </div>
  );
}

/** The top-bar bell's inbox: read and clear notifications without leaving the current page. */
export function NotificationsDialog({ onClose }: { onClose: () => void }) {
  const [unreadOnly, setUnreadOnly] = useState(true);
  const inbox = useInbox(unreadOnly, onClose);
  return (
    <Modal
      title="Notifications"
      onClose={onClose}
      actions={
        <>
          <Link to="/notifications" onClick={onClose} style={{ marginRight: 'auto', alignSelf: 'center' }}>
            View all
          </Link>
          <BrowserNotificationButton size="sm" />
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
          <Button onClick={() => inbox.readAll.mutate(undefined)} loading={inbox.readAll.isPending}>
            Mark all as read
          </Button>
        </>
      }
    >
      <InboxTabs unreadOnly={unreadOnly} unread={inbox.q.data?.pages[0]?.unread} onChange={setUnreadOnly} />
      <InboxList inbox={inbox} unreadOnly={unreadOnly} />
    </Modal>
  );
}
